import { useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';

/**
 * Tawk.to Live Chat Integration
 * 
 * Privacy & Security Notes:
 * - Tawk.to is GDPR compliant and SOC 2 Type II certified
 * - End-to-end encryption is enabled
 * - Consent form can be enabled in Tawk.to dashboard
 * - Data is processed per Tawk.to's Privacy Policy: https://www.tawk.to/privacy-policy/
 * 
 * To configure:
 * 1. Create a free account at https://www.tawk.to
 * 2. Get your Property ID and Widget ID from the dashboard
 * 3. Add them to admin settings or replace the defaults below
 */

const TawkToChat = () => {
  const { settings } = useSettings();
  
  useEffect(() => {
    // Only load if enabled in settings (default to enabled if setting doesn't exist)
    const isEnabled = settings.tawkto_enabled !== false;
    
    if (!isEnabled) return;
    
    // Get property and widget IDs from settings or use placeholders
    const propertyId = settings.tawkto_property_id || 'PROPERTY_ID_PLACEHOLDER';
    const widgetId = settings.tawkto_widget_id || 'WIDGET_ID_PLACEHOLDER';
    
    // Don't load if placeholders are still in use
    if (propertyId === 'PROPERTY_ID_PLACEHOLDER' || widgetId === 'WIDGET_ID_PLACEHOLDER') {
      console.log('Tawk.to: Configure property and widget IDs in admin settings to enable live chat');
      return;
    }
    
    // Check if script is already loaded
    if (window.Tawk_API) return;
    
    // Initialize Tawk.to
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    
    // Create and append script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    
    // Configure Tawk.to API before load
    window.Tawk_API.onLoad = function() {
      console.log('Tawk.to chat widget loaded');
      
      // Optional: Customize the widget
      // window.Tawk_API.setAttributes({
      //   'name': 'Visitor',
      //   'store': 'HomeLyph'
      // }, function(error) {});
    };
    
    // Handle errors
    script.onerror = () => {
      console.error('Failed to load Tawk.to chat widget');
    };
    
    document.head.appendChild(script);
    
    // Cleanup on unmount
    return () => {
      // Remove the script tag
      const existingScript = document.querySelector(`script[src*="embed.tawk.to"]`);
      if (existingScript) {
        existingScript.remove();
      }
      
      // Clean up Tawk.to globals
      delete window.Tawk_API;
      delete window.Tawk_LoadStart;
      
      // Remove Tawk.to iframe if it exists
      const tawkFrame = document.querySelector('iframe[title*="chat"]');
      if (tawkFrame) {
        tawkFrame.remove();
      }
    };
  }, [settings.tawkto_enabled, settings.tawkto_property_id, settings.tawkto_widget_id]);
  
  return null; // This component doesn't render anything visible
};

export default TawkToChat;
